import React from 'react'
import PropTypes from 'prop-types'
import { Box, Divider, Typography } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled, useTheme } from '@mui/material/styles'
import loadable from '@loadable/component' // Import loadable
import { useInView } from 'react-intersection-observer'

import waveBot from '../../images/bottomwave.png'
import navyBkg from '../../images/navy-bkg.jpg'
import waveTop from '../../images/topwave-flipped.png'
import ContentWrapper from '../ContentWrapper'

// Dynamically import CountUp using loadable
const LoadableCountUp = loadable(() => import('react-countup'), {
  fallback: <span>0</span>, // Fallback content while loading
  ssr: false, // Disable SSR for this component
})

// Styled Components
const BlueContainer = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${waveTop}), url(${waveBot}), url(${navyBkg})`,
  backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
  backgroundPosition: 'top center, bottom center, center',
  backgroundSize: '100% 100px, 100% 100px, 100% 100%',
  display: 'flex',
  alignItems: 'center',
  padding: '170px 5%',
  color: 'white',
  fontSize: '18px',
  lineHeight: '32px',
  fontWeight: 300,
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    backgroundSize: '100% 40px, 100% 40px, 100% 100%',
    padding: '70px 0',
  },
}))

const TableContainer = styled(Box)(({ theme }) => ({
  border: '1px solid rgba(216, 180, 90, 0.5)',
  borderRadius: 48,
}))

const TableHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
  [theme.breakpoints.down('sm')]: {
    padding: 15,
  },
}))

const TableBody = styled(Box)(({ theme }) => ({
  display: 'flex',
}))

const Item = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFull',
})(({ theme, isFull }) => ({
  padding: 40,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  width: isFull ? '100%' : '50%',
  gap: 8,
  '&:not(:last-child)': {
    borderRight: isFull ? 'none' : '1px solid rgba(216, 180, 90, 0.5)',
  },
  '& img': {
    maxWidth: '40px',
  },
  '& span': {
    fontFamily: 'Poppins',
    fontSize: 64,
    lineHeight: 1,
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      fontSize: 32,
    },
  },
  '& label': {
    fontSize: 16,
    lineHeight: '24px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      lineHeight: '18px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: 24,
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontSize: 40,
  lineHeight: '46px',
  fontWeight: 600,
  padding: '0 40px',
  color: theme.palette.secondary.base,
  [theme.breakpoints.down('sm')]: {
    padding: 0,
    fontSize: 32,
  },
}))

const DividerLine = styled(Divider)(({ theme }) => ({
  backgroundColor: 'rgba(216, 180, 90, 0.5)',
}))

const Footer = styled(Box)(({ theme }) => ({
  fontSize: 12,
  lineHeight: '18px',
  width: '100%',
  padding: '6px 24px',
  textAlign: 'center',
}))

const createFooterHtml = (footerHtml) => ({ __html: footerHtml })

const GenerousInfographic = ({ data }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Intersection Observer
  const { ref, inView } = useInView({
    triggerOnce: true, // Trigger only once when visible
    threshold: 0.2, // 20% of the element needs to be visible
  })

  const footerHtml = createFooterHtml(data.footer_html)

  return (
    <BlueContainer>
      <ContentWrapper size="small">
        <div ref={ref}>
          <TableContainer>
            <TableHead>
              <DividerLine flex={1} />
              <Title>{data?.title}</Title>
              <DividerLine flex={1} />
            </TableHead>
            <DividerLine />
            {data?.rows?.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <TableBody>
                  {row.columns.map((column, colIndex) => (
                    <Item key={colIndex} isFull={row.columns.length === 1}>
                      <img
                        src={column.icon?.localAsset?.publicURL}
                        alt={column.caption}
                      />
                      {inView ? (
                        <Typography variant="h2" component="div">
                          <LoadableCountUp
                            start={0}
                            end={Number(column.number_value) || 0}
                            duration={2}
                            prefix={column.number_prefix?.substr(0, 3) || ''}
                            suffix={column.number_suffix?.substr(0, 3) || ''}
                          />
                        </Typography>
                      ) : (
                        <span>
                          {column.number_prefix?.substr(0, 3) || ''}0
                          {column.number_suffix?.substr(0, 3) || ''}
                        </span>
                      )}
                      <label>{column.caption}</label>
                    </Item>
                  ))}
                </TableBody>
                {rowIndex !== data.rows.length - 1 && <DividerLine />}
              </React.Fragment>
            ))}
          </TableContainer>
        </div>
      </ContentWrapper>
      <Footer>
        {data?.footer_html ? (
          <div dangerouslySetInnerHTML={footerHtml} />
        ) : (
          <>
            The above figures represent Irrational Capital’s full data set. A
            subset of this data is used in constructing HAPY.
          </>
        )}
      </Footer>
    </BlueContainer>
  )
}

GenerousInfographic.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        columns: PropTypes.arrayOf(
          PropTypes.shape({
            icon: PropTypes.object,
            number_value: PropTypes.any,
            number_prefix: PropTypes.string,
            number_suffix: PropTypes.string,
            caption: PropTypes.string,
          })
        ),
      })
    ),
    footer_html: PropTypes.string,
  }).isRequired,
}

export default GenerousInfographic
